import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldAlert, ShieldCheck, ChevronDown, ChevronRight, Loader2, AlertTriangle,
  TrendingDown, CheckCircle2, XCircle, Plus, Gavel, Info,
} from "lucide-react";

const SEV = {
  critical: "bg-red-100 text-red-700 border-red-300",
  high: "bg-orange-100 text-orange-700 border-orange-300",
  medium: "bg-amber-50 text-amber-700 border-amber-200",
  low: "bg-zinc-100 text-zinc-600 border-zinc-200",
};
const CATS = ["identity", "forms", "section_k", "pricing", "pop", "past_performance", "hedge",
  "formatting", "compliance", "other"];

/* Per-item HUMAN OVERRIDE (logged; the machine never auto-clears). */
function OverrideRow({ dealId, item, overridden, onDone }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!reason.trim()) { toast.error("A typed reason is required to override."); return; }
    setBusy(true);
    try {
      await api.post(`/bids/${dealId}/gate-override`, { item, reason: reason.trim() });
      toast.success("Overridden — logged & marked NOT auto-verified.");
      setOpen(false); setReason(""); onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Override failed.");
    } finally { setBusy(false); }
  };
  return (
    <li className="text-[12px] flex flex-col gap-1 border-b border-zinc-100 pb-2" data-testid="package-truth-item">
      <div className="flex gap-1.5 items-start">
        {overridden
          ? <ShieldCheck className="h-3.5 w-3.5 text-amber-500 shrink-0 mt-0.5" />
          : <ShieldAlert className="h-3.5 w-3.5 text-red-500 shrink-0 mt-0.5" />}
        <span className={`min-w-0 ${overridden ? "text-amber-700 line-through/0" : "text-zinc-700"}`}>
          {item}
          {overridden && <span className="ml-1 text-[10px] font-semibold text-amber-600">(human-overridden — NOT auto-verified)</span>}
        </span>
        {!overridden && (
          <button onClick={() => setOpen((o) => !o)} data-testid="override-toggle"
            className="ml-auto shrink-0 text-[10px] text-zinc-500 border border-zinc-200 rounded px-1.5 py-0.5 hover:bg-zinc-50">
            Override
          </button>
        )}
      </div>
      {open && !overridden && (
        <div className="flex gap-1.5 pl-5">
          <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="override-reason"
            placeholder="Typed reason (logged: who / what / when)"
            className="flex-1 text-[11px] border border-zinc-300 rounded px-2 py-1" />
          <button onClick={submit} disabled={busy} data-testid="override-submit"
            className="text-[11px] bg-zinc-900 text-white px-2.5 py-1 rounded hover:bg-zinc-800 disabled:opacity-50">
            {busy ? "…" : "Log override"}
          </button>
        </div>
      )}
    </li>
  );
}

/* Intake — log a NEW finding to this bid (optionally promote to a global rule). */
function FindingIntake({ dealId, onLogged }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ category: "other", severity: "medium", where: "", what_wrong: "", what_right: "", rule: "", promote_global: false });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.what_wrong.trim()) { toast.error("Describe what's wrong (one line)."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/bids/${dealId}/findings`, f);
      toast.success(data.promoted_global ? "Finding logged + promoted to a global rule." : "Finding logged on this bid.");
      setF({ category: "other", severity: "medium", where: "", what_wrong: "", what_right: "", rule: "", promote_global: false });
      setOpen(false); onLogged && onLogged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not log finding.");
    } finally { setBusy(false); }
  };
  return (
    <div className="border border-zinc-200 rounded-lg p-3 bg-zinc-50" data-testid="finding-intake">
      <button onClick={() => setOpen((o) => !o)} className="flex items-center gap-1.5 text-[12px] font-semibold text-zinc-700" data-testid="finding-intake-toggle">
        <Plus className="h-3.5 w-3.5" /> Share an audit finding with the system
      </button>
      {open && (
        <div className="mt-2 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="finding-category"
              className="text-[11px] border border-zinc-300 rounded px-2 py-1">
              {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={f.severity} onChange={(e) => setF({ ...f, severity: e.target.value })} data-testid="finding-severity"
              className="text-[11px] border border-zinc-300 rounded px-2 py-1">
              {["critical", "high", "medium", "low"].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <input value={f.where} onChange={(e) => setF({ ...f, where: e.target.value })} placeholder="Where (page / section / form)"
            data-testid="finding-where" className="w-full text-[11px] border border-zinc-300 rounded px-2 py-1" />
          <input value={f.what_wrong} onChange={(e) => setF({ ...f, what_wrong: e.target.value })} placeholder="What's wrong (one line) *"
            data-testid="finding-what-wrong" className="w-full text-[11px] border border-zinc-300 rounded px-2 py-1" />
          <input value={f.what_right} onChange={(e) => setF({ ...f, what_right: e.target.value })} placeholder="What right looks like"
            data-testid="finding-what-right" className="w-full text-[11px] border border-zinc-300 rounded px-2 py-1" />
          <input value={f.rule} onChange={(e) => setF({ ...f, rule: e.target.value })} placeholder="The rule to prevent recurrence"
            data-testid="finding-rule" className="w-full text-[11px] border border-zinc-300 rounded px-2 py-1" />
          <label className="flex items-center gap-1.5 text-[11px] text-zinc-600">
            <input type="checkbox" checked={f.promote_global} onChange={(e) => setF({ ...f, promote_global: e.target.checked })} data-testid="finding-promote" />
            Promote to a GLOBAL rule (applies to every future proposal)
          </label>
          <button onClick={submit} disabled={busy} data-testid="finding-submit"
            className="text-[11px] bg-gold-dark text-white px-3 py-1.5 rounded hover:opacity-90 disabled:opacity-50">
            {busy ? "Logging…" : "Log finding"}
          </button>
        </div>
      )}
    </div>
  );
}

/* Dir 144 Part 0 — WD-authored DRAFT deliverables: officer confirm-to-clear (logged review flips the send
   blocker; the DRAFT watermark stays on the file until finalized — nothing auto-certifies). */
function WdDraftReviewCard({ dealId, items, onChanged }) {
  const [busy, setBusy] = useState(null);
  if (!items || items.length === 0) return null;
  const act = async (key, action) => {
    setBusy(key);
    try {
      await api.post(`/bids/${dealId}/wd-drafts/${key}/review`, { action });
      toast.success(action === "unreview"
        ? "Review cleared — DRAFT is blocking again."
        : "DRAFT reviewed & confirmed — send blocker cleared (logged).");
      onChanged && onChanged();
      window.dispatchEvent(new Event("ironclad-refresh"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update review.");
    } finally { setBusy(null); }
  };
  const pending = items.filter((i) => !i.reviewed).length;
  return (
    <div className="rounded-lg border border-zinc-200 p-3" data-testid="wd-draft-review-card">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] font-bold text-zinc-700 uppercase tracking-wide">
          WD-authored DRAFTs — officer confirm-to-clear
        </div>
        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${pending === 0 ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`} data-testid="wd-draft-status">
          {pending === 0 ? "All reviewed" : `${pending} pending`}
        </span>
      </div>
      <ul className="space-y-1.5">
        {items.map((it) => (
          <li key={it.key} className="text-[12px] flex items-center gap-2" data-testid={`wd-draft-row-${it.key}`}>
            {it.reviewed
              ? <ShieldCheck className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
              : <ShieldAlert className="h-3.5 w-3.5 text-amber-500 shrink-0" />}
            <span className="min-w-0 flex-1 text-zinc-700 truncate" title={it.title}>{it.title}</span>
            {it.reviewed ? (
              <>
                <span className="text-[10px] text-emerald-600 shrink-0" title={`${it.reviewed_by} @ ${it.reviewed_at}${it.note ? " — " + it.note : ""}`}>
                  confirmed
                </span>
                <button onClick={() => act(it.key, "unreview")} disabled={busy === it.key} data-testid={`wd-draft-unreview-${it.key}`}
                  className="shrink-0 text-[10px] text-zinc-500 border border-zinc-200 rounded px-1.5 py-0.5 hover:bg-zinc-50 disabled:opacity-50">
                  Undo
                </button>
              </>
            ) : (
              <button onClick={() => act(it.key, "review")} disabled={busy === it.key} data-testid={`wd-draft-review-${it.key}`}
                className="shrink-0 text-[11px] bg-zinc-900 text-white px-2.5 py-1 rounded hover:bg-zinc-800 disabled:opacity-50">
                {busy === it.key ? "…" : "Mark reviewed"}
              </button>
            )}
          </li>
        ))}
      </ul>
      <div className="text-[10px] text-zinc-400 mt-1">These are WD's OWN draft documents (facts-only, DRAFT-watermarked). Your logged review clears the send blocker — the machine never auto-certifies them, and the watermark stays until the file is finalized.</div>
    </div>
  );
}

/* Directive 55 Part 6 — PACKAGE TRUTH panel (6A: consolidated gate findings + per-item override) +
   WHY BIDS WERE LOST insights card (6B: live clears/trips from the critique pass, never a static poster). */
export const PackageTruth = ({ dealId }) => {
  const [data, setData] = useState(null);
  const [certs, setCerts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get(`/bids/${dealId}/send-readiness`).then((r) => r.data).catch(() => null),
      api.get(`/bids/${dealId}/cert-set`).then((r) => r.data).catch(() => null),
    ]).then(([sr, cs]) => { setData(sr); setCerts(cs); }).finally(() => setLoading(false));
  }, [dealId]);

  const attest = async (cert) => {
    try {
      await api.post(`/bids/${dealId}/certs/attest`, { cert });
      toast.success(`Attested ${cert} — logged.`);
      api.get(`/bids/${dealId}/cert-set`).then((r) => setCerts(r.data)).catch(() => {});
    } catch (e) { toast.error(e?.response?.data?.detail || "Attest failed."); }
  };

  useEffect(() => {
    load();
    const onRefresh = () => load();
    window.addEventListener("ironclad-refresh", onRefresh);
    return () => window.removeEventListener("ironclad-refresh", onRefresh);
  }, [load]);

  if (loading && !data) {
    return (
      <div className="mb-5 rounded-xl border border-zinc-200 bg-white px-4 py-3 flex items-center gap-2 text-sm text-zinc-500" data-testid="package-truth-panel">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading Package Truth…
      </div>
    );
  }
  if (!data) return null;

  const annotated = data.annotated_blockers || [];
  const crit = data.critique || {};
  const defects = crit.defects || [];
  const defectTrips = crit.defect_trips ?? 0;
  const defectClears = crit.defect_clears ?? 0;
  const criticalTrips = (crit.critical_hits || []).length;
  const activeCount = data.active_count ?? 0;

  return (
    <div className="mb-5 rounded-xl border border-zinc-200 bg-white shadow-sm overflow-hidden" data-testid="package-truth-panel">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-3 px-4 py-3 text-left" data-testid="package-truth-toggle">
        <Gavel className="h-5 w-5 text-zinc-700 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-zinc-800">Package Truth &amp; Critique</div>
          <div className="text-[11px] text-zinc-500 mt-0.5">
            Defects: <b className="text-emerald-600">{defectClears} cleared</b> · <b className="text-red-600">{defectTrips} tripped</b> (counted once each)
          </div>
        </div>
        {criticalTrips > 0 && (
          <span className="text-[10px] font-bold text-red-700 bg-red-100 border border-red-300 rounded px-2 py-0.5" data-testid="disqualification-warning">
            DISQUALIFICATION RISK
          </span>
        )}
        {open ? <ChevronDown className="h-4 w-4 text-zinc-400" /> : <ChevronRight className="h-4 w-4 text-zinc-400" />}
      </button>

      {open && (
        <div className="px-4 pb-4 pt-1 space-y-4 border-t border-zinc-200 bg-card" data-testid="package-truth-detail">
          {criticalTrips > 0 && (
            <div className="rounded-lg border border-red-300 bg-red-100/60 p-3 text-[12px] text-red-800 flex gap-2" data-testid="disqualification-banner">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span><b>{criticalTrips} critical finding{criticalTrips === 1 ? "" : "s"}</b> match a known cause of rejection — the send gate is hard-blocked until resolved or human-overridden.</span>
            </div>
          )}

          {/* 6B — WHY BIDS WERE LOST insights card (deduped defects; counted once, all rules listed) */}
          <div className="rounded-lg border border-zinc-200 p-3" data-testid="why-lost-card">
            <div className="flex items-center gap-1.5 text-[11px] font-bold text-zinc-700 uppercase tracking-wide mb-2">
              <TrendingDown className="h-3.5 w-3.5 text-zinc-500" /> Why bids were lost — this package
            </div>
            <ul className="space-y-1.5">
              {defects.map((d, i) => (
                <li key={i} className="text-[12px] flex gap-1.5 items-start" data-testid={`why-lost-item-${i}`}>
                  {d.tripped === true
                    ? <XCircle className="h-3.5 w-3.5 text-red-500 shrink-0 mt-0.5" />
                    : d.tripped === false
                      ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0 mt-0.5" />
                      : <Info className="h-3.5 w-3.5 text-zinc-300 shrink-0 mt-0.5" />}
                  <span className="min-w-0 text-zinc-700">
                    <b>{(d.rules || []).join(" / ")}</b> — {d.what_wrong}
                    {["critical", "high"].includes(d.severity) && d.tripped === true &&
                      <span className="ml-1 text-[9px] font-bold uppercase rounded px-1 py-0.5 border align-middle bg-orange-100 text-orange-700 border-orange-300">{d.severity} · gates</span>}
                    {d.tripped === null && <span className="ml-1 text-[10px] text-zinc-400">(advisory)</span>}
                    {d.weight > 0 && <span className="ml-1 text-[10px] text-orange-500 font-semibold">↑ weighted (loss history)</span>}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Item 2 — CERT SET for human attestation (from certs.py; UI never recomputes an answer) */}
          {certs && (certs.certs || []).length > 0 && (
            <div className="rounded-lg border border-zinc-200 p-3" data-testid="cert-set">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] font-bold text-zinc-700 uppercase tracking-wide">
                  Reps &amp; Certs — human attestation
                </div>
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${certs.all_attested ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`} data-testid="cert-set-status">
                  {certs.all_attested ? "All attested" : `${certs.pending.length} pending`}
                </span>
              </div>
              <ul className="space-y-1">
                {certs.certs.map((c, i) => (
                  <li key={i} className="text-[12px] flex items-center gap-2" data-testid={`cert-row-${c.cert}`}>
                    <input type="checkbox" checked={!!c.attested} disabled={!!c.attested}
                      onChange={() => attest(c.cert)} data-testid={`cert-attest-${c.cert}`} className="shrink-0" />
                    <span className="font-mono text-[11px] text-zinc-500 w-16 shrink-0">{c.cert}</span>
                    <span className="min-w-0 flex-1 text-zinc-700 truncate" title={c.meaning}>{c.meaning}</span>
                    <span className="text-[11px] font-semibold text-zinc-900 shrink-0">{c.answer}</span>
                    {c.attested
                      ? <span className="text-[10px] text-emerald-600 shrink-0" title={`${c.attested_by} @ ${c.attested_at}`}>affirmed</span>
                      : <span className="text-[10px] text-amber-600 shrink-0">pending</span>}
                  </li>
                ))}
              </ul>
              <div className="text-[10px] text-zinc-400 mt-1">The cert gate still hard-blocks a false/ambiguous/unanswered cert — this only records the human affirmation of a signed legal statement.</div>
            </div>
          )}

          {/* Dir 144 Part 0 — WD-authored DRAFT deliverables: officer confirm-to-clear */}
          <WdDraftReviewCard dealId={dealId} items={data.wd_authored || []} onChanged={load} />

          {/* Part 3 — full critique findings (severity / where / fix) */}
          <div data-testid="critique-findings">
            <div className="text-[11px] font-bold text-zinc-700 uppercase tracking-wide mb-2">
              Critique findings <span className="text-zinc-400">({crit.trips || 0} tripped · {crit.clears || 0} cleared · {crit.advisory || 0} advisory)</span>
            </div>
            <ul className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
              {(crit.findings || []).filter((r) => r.tripped !== false).map((r, i) => (
                <li key={i} className="text-[12px] flex gap-1.5 items-start" data-testid={`critique-finding-${i}`}>
                  <span className={`text-[9px] font-bold uppercase rounded px-1 py-0.5 border shrink-0 ${SEV[r.severity] || SEV.low}`}>{r.severity}</span>
                  <span className="min-w-0 text-zinc-700">
                    {r.what_wrong}
                    {r.where && <span className="text-zinc-400"> · {r.where}</span>}
                    {r.tripped === true && <span className="ml-1 text-[10px] text-red-600 font-semibold">TRIPPED</span>}
                    <div className="text-[11px] text-zinc-500">Fix: {r.fix}</div>
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* 6A — consolidated gate findings with per-item human override (incl. HIGH/CRITICAL critique) */}
          {annotated.length > 0 && (
            <div data-testid="gate-findings">
              <div className="text-[11px] font-bold text-zinc-700 uppercase tracking-wide mb-2">
                Send-gate findings <span className="text-zinc-400">({activeCount} active · {annotated.length - activeCount} overridden)</span>
              </div>
              <ul className="space-y-2">
                {annotated.map((a, i) => (
                  <OverrideRow key={i} dealId={dealId} item={a.blocker} overridden={a.overridden} onDone={() => { load(); window.dispatchEvent(new Event("ironclad-refresh")); }} />
                ))}
              </ul>
              <div className="text-[10px] text-zinc-400 mt-1">The machine never auto-clears an item — only a logged human override does. HIGH &amp; CRITICAL critique hits hold the gate; never-auto-send still applies.</div>
            </div>
          )}

          <FindingIntake dealId={dealId} onLogged={load} />
        </div>
      )}
    </div>
  );
};

export default PackageTruth;
